// poImportDraft.ts — the reviewer's working copy of a staged import batch, and
// the pure functions that build it, resolve vendors against it, and turn it
// back into a confirm() payload.
//
// `StagedDocument` (poImportApi.ts) is what the server proposed and never
// changes after parse(). `DocDraft` is what the reviewer is editing. Keeping
// them separate matters for exactly one reason: an AI-read document's confirm
// payload must echo the ORIGINAL extraction verbatim (`staged_extraction`) so
// the server can compute an AI-learning delta against what actually got
// confirmed — if the draft and the original were the same object, editing the
// draft would corrupt the training signal.

import {
  isAiSource,
  normalizeVendorName,
  PRODUCT_LABEL,
  type ApiErrorBody,
  type ConfirmDocumentInput,
  type ConfirmVendorCreate,
  type ImportLine,
  type ImportSource,
  type ImportStatus,
  type StagedDocument,
  type VendorCreateProposal,
  type VendorResolution,
} from './poImportApi';
// The needs-review gate's grammar, straight from its canonical home. poImportApi
// re-exports the same symbols for this lane's pages and tests.
import {
  reviewKey,
  type ConfirmReviewInput,
  type ReviewRequiredEntry,
  type ReviewResolution,
} from '../components/review/grounding';
import { buildVendorGroups, suggestExistingVendors, type VendorGroup, type VendorGroupInput } from './poImportVendorGroups';
// Type-only: VendorPreviewModal carries no logic of its own into this module,
// and the import is erased at compile time — just the shape both the document
// card and the vendor triage panel need to open the same preview.
import type { VendorPreviewSubject } from './VendorPreviewModal';

export interface VendorOption {
  id: string;
  name: string;
  type: string;
  is_disabled: boolean;
}

export interface DocDraft {
  key: string; // source_filename — unique within a parse batch
  original: StagedDocument;
  include: boolean;
  // Set when the batch builder defaulted `include` to false with a reason the
  // reviewer should see before flipping it back on (already-imported PO,
  // nothing to import, a revision's own base file).
  autoExcludedReason: string | null;

  status: ImportStatus;
  poNumber: string;
  orderDate: string; // '' = missing, the one hard blocker besides vendor
  quoteNumber: string;
  shipDate: string;
  shipVia: string;
  terms: string;
  shipToName: string;
  shipToAttn: string;
  shipToAddress: string;
  notes: string;
  subtotalCents: number;

  lines: ImportLine[];

  // legacy purchase_order_lines.id -> checked. Default: every offered line
  // checked (spec: "checkbox per line (default CHECKED)").
  attachSelected: Record<string, boolean>;

  // Vendor decision inputs. See resolveEffectiveVendor below for how these
  // combine with the batch-wide vendor groups.
  manualVendorName: string; // typed only when the document named no vendor at all
  existingVendorOverrideId: string; // '' = no override; else pins to a specific vendors.id

  // Rare, manual-only: see the "amendment" note in PoImportDocumentCard for why
  // this is never auto-filled from doc.amendment.
  supersedesPoNumber: string;

  // reviewKey -> how the person answered it. Lives on the DRAFT, not on the
  // card, so an answer survives collapsing the document and re-opening it, and
  // so the batch bar can count what is still waiting without expanding anything.
  reviews: Record<string, ReviewResolution>;

  // "Yes, this really is the other product's purchase order and I want it here."
  // Only ever consulted when original.foreign_entity is set. Starts false so the
  // reviewer opts in, and lives on the draft for the same reason `reviews` does:
  // the batch bar counts what is still waiting without expanding a card.
  foreignAck: boolean;
}

function cloneLines(lines: ImportLine[]): ImportLine[] {
  return lines.map((l) => ({ ...l }));
}

/**
 * Build the initial working drafts for every READY document in a parse batch.
 *
 * Two passes: the first builds each document's draft independently; the
 * second looks across all of them to default OFF a document that would be a
 * structural no-op if confirmed as-is (already exists with nothing to attach;
 * the base file of a revision that is ALSO in this batch — see the amendment
 * note). Nothing here is destructive — every default is a starting point the
 * reviewer can flip back on with one click.
 */
export function buildInitialDrafts(documents: StagedDocument[]): DocDraft[] {
  const ready = documents.filter((d) => d.status === 'ready' && d.extraction);

  const drafts: DocDraft[] = ready.map((doc) => {
    const ex = doc.extraction as NonNullable<StagedDocument['extraction']>;
    const attachSelected: Record<string, boolean> = {};
    if (doc.attach_offer) {
      for (const line of doc.attach_offer.lines) attachSelected[line.id] = true;
    }

    const hasLines = ex.lines.length > 0;
    const hasAttach = !!doc.attach_offer && doc.attach_offer.lines.length > 0;

    let include = true;
    let autoExcludedReason: string | null = null;
    if (doc.duplicate && !hasAttach) {
      include = false;
      autoExcludedReason = `${ex.po_number} already exists in this database. Importing this document again would create nothing new, so nothing here is lost by leaving it unchecked.`;
    } else if (!hasLines && !hasAttach) {
      include = false;
      autoExcludedReason = 'This document has no line items and nothing to attach, so nothing would be created.';
    }

    return {
      key: doc.source_filename,
      original: doc,
      include,
      autoExcludedReason,
      status: 'closed',
      poNumber: ex.po_number,
      orderDate: ex.order_date || '',
      quoteNumber: ex.quote_number || '',
      shipDate: ex.ship_date || '',
      shipVia: ex.ship_via || '',
      terms: ex.terms || '',
      shipToName: ex.ship_to_name || '',
      shipToAttn: ex.ship_to_attn || '',
      shipToAddress: ex.ship_to_address || '',
      notes: '',
      subtotalCents: ex.subtotal_cents ?? 0,
      lines: cloneLines(ex.lines),
      attachSelected,
      manualVendorName: '',
      existingVendorOverrideId: '',
      supersedesPoNumber: '',
      reviews: {},
      foreignAck: false,
    };
  });

  // Second pass: a revision document whose base is ALSO in this batch — the
  // base file, left as itself, would collide with the revision on the SAME
  // po_number (the importer's canonical PO number strips any -R1 suffix, so a
  // revision never gets a distinct number the way an in-app Amend does). The
  // two can never both be confirmed together (the server 409s the whole batch
  // on a repeated number), so default off whichever one restates the OLDER
  // story: the base file, not the revision that superseded it.
  for (const revisionDraft of drafts) {
    const amendment = revisionDraft.original.amendment;
    if (!amendment || !amendment.base_in_batch) continue;
    const baseDraft = drafts.find(
      (d) => d.key !== revisionDraft.key && d.poNumber === amendment.base_po_number && !d.original.extraction?.revision
    );
    if (baseDraft && baseDraft.include) {
      baseDraft.include = false;
      baseDraft.autoExcludedReason = `Revision ${amendment.revision} of ${amendment.base_po_number} is also in this batch and supersedes this file. The two share one purchase-order number, so only one of them can be imported: the revision is checked, this original is not.`;
    }
  }

  return drafts;
}

// ---------------------------------------------------------------------------
// Vendor resolution
// ---------------------------------------------------------------------------

export interface VendorGroupResolution {
  mode: 'create' | 'existing';
  name: string;
  type: string;
  isDisabled: boolean;
  existingVendorId: string;
}

export function defaultGroupResolution(group: VendorGroup): VendorGroupResolution {
  return {
    mode: 'create',
    name: group.nameOptions[0]?.name || group.seedFields.name,
    type: '',
    isDisabled: group.defaultDisabled,
    existingVendorId: '',
  };
}

/** Whether a document named no vendor at all — the "needs-attention red" case. */
function isNameless(vr: VendorResolution | null | undefined): boolean {
  return !vr || (!vr.matched_vendor_id && !vr.create_proposal && !vr.document_name);
}

/**
 * Every group input across the batch: real create_proposal documents, PLUS a
 * synthetic entry for any nameless document the reviewer has typed a vendor
 * name into (folding it into the same one-decision-per-group pipeline instead
 * of a second, separate resolution UI).
 */
export function buildGroupInputs(drafts: DocDraft[], vendorList: VendorOption[]): VendorGroupInput[] {
  const inputs: VendorGroupInput[] = [];
  for (const d of drafts) {
    if (d.existingVendorOverrideId) continue; // already resolved to an existing vendor, out of the pool
    const vr = d.original.vendor_resolution;
    if (vr?.create_proposal) {
      inputs.push({ docKey: d.key, createProposal: vr.create_proposal, suggestions: vr.suggestions || [] });
    } else if (isNameless(vr) && d.manualVendorName.trim()) {
      const name = d.manualVendorName.trim();
      inputs.push({
        docKey: d.key,
        createProposal: {
          name,
          // normalizeVendorName, NOT toLowerCase: buildVendorGroups keys on
          // normalized_name, and the server's proposals arrive already
          // suffix-stripped. Lowercasing alone left a typed "Acme Corp." keyed
          // as "acme corp." while the server's own "Acme" proposal keyed as
          // "acme" — two triage rows for one vendor, and the containment merge
          // could not close the gap because the punctuation is still in there.
          normalized_name: normalizeVendorName(name),
          vendor_type: null,
          is_disabled: false,
          is_disabled_reason: null,
          latest_order_date: d.original.extraction?.order_date ?? null,
          po_count_in_batch: 1,
        },
        suggestions: suggestExistingVendors(name, vendorList),
      });
    }
  }
  return inputs;
}

/** All of a batch's vendor groups, keyed for O(1) per-document lookup. */
export function computeVendorGroups(drafts: DocDraft[], vendorList: VendorOption[]): VendorGroup[] {
  return buildVendorGroups(buildGroupInputs(drafts, vendorList));
}

export function groupsByDocKey(groups: VendorGroup[]): Map<string, VendorGroup> {
  const map = new Map<string, VendorGroup>();
  for (const group of groups) {
    for (const docKey of group.docKeys) map.set(docKey, group);
  }
  return map;
}

export type VendorEffective =
  | { kind: 'matched'; vendorId: string; label: string }
  | { kind: 'manual-existing'; vendorId: string; label: string }
  | { kind: 'group'; group: VendorGroup; resolved: boolean; resolution: VendorGroupResolution; label: string }
  | { kind: 'needs-name'; label: '' };

export function resolveEffectiveVendor(
  draft: DocDraft,
  groupByDocKey: Map<string, VendorGroup>,
  resolutions: Record<string, VendorGroupResolution>
): VendorEffective {
  const vr = draft.original.vendor_resolution;

  if (draft.existingVendorOverrideId) {
    return {
      kind: vr?.matched_vendor_id ? 'matched' : 'manual-existing',
      vendorId: draft.existingVendorOverrideId,
      label: draft.existingVendorOverrideId,
    };
  }
  if (vr?.matched_vendor_id) {
    return { kind: 'matched', vendorId: vr.matched_vendor_id, label: vr.matched_name || '' };
  }
  const group = groupByDocKey.get(draft.key);
  if (group) {
    const resolution = resolutions[group.groupKey] || defaultGroupResolution(group);
    const resolved =
      (resolution.mode === 'existing' && !!resolution.existingVendorId) ||
      (resolution.mode === 'create' && !!resolution.type);
    return { kind: 'group', group, resolved, resolution, label: resolution.name };
  }
  return { kind: 'needs-name', label: '' };
}

// ---------------------------------------------------------------------------
// The not-sure gate
// ---------------------------------------------------------------------------
//
// The server decides WHAT must be looked at (po-import-service.js
// computeReviewRequired) and refuses the batch if anything on that list arrives
// unanswered. Everything here is the reviewer's side of the same list: which
// entries are still open, and the two ways to close one.

/** The list the server staged for this document. Always [] for xlsx. */
export function reviewEntries(draft: DocDraft): ReviewRequiredEntry[] {
  return draft.original.review_required || [];
}

/** Entries grouped by the ONE thing that clears them (reviewKey). */
export function reviewEntriesByKey(draft: DocDraft): Map<string, ReviewRequiredEntry[]> {
  const map = new Map<string, ReviewRequiredEntry[]>();
  for (const entry of reviewEntries(draft)) {
    const key = reviewKey(entry);
    const found = map.get(key);
    if (found) found.push(entry);
    else map.set(key, [entry]);
  }
  return map;
}

/**
 * The places still waiting, counted the way a person counts them: once per
 * place, not once per complaint. Two invented strings on one line is one look.
 */
export function unreconciledReviewKeys(draft: DocDraft): string[] {
  return [...reviewEntriesByKey(draft).keys()].filter((key) => !draft.reviews[key]);
}

/**
 * Answer every entry sharing a key at once.
 *
 * `edited` is not value-checked, here or on the server: the acknowledgement is
 * the point. A reviewer who typed into a flagged field and then typed the
 * original value back has still looked at it, which is the whole ask.
 */
export function resolveReviews(
  draft: DocDraft,
  keys: string[],
  resolution: ReviewResolution
): Record<string, ReviewResolution> {
  const next = { ...draft.reviews };
  for (const key of keys) next[key] = resolution;
  return next;
}

/** The payload half: one review per place, whether it was edited or accepted. */
function reviewPayload(draft: DocDraft): ConfirmReviewInput[] {
  const out: ConfirmReviewInput[] = [];
  for (const [key, entries] of reviewEntriesByKey(draft)) {
    const resolution = draft.reviews[key];
    if (!resolution) continue;
    const { target, field, line_number: lineNumber } = entries[0];
    out.push({ target, field: target === 'line' ? null : field, line_number: lineNumber, resolution });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Blocking reasons — computed client-side so a doc that cannot possibly pass
// confirm()'s validation never gets the chance to abort the WHOLE batch (all
// documents validate before the transaction opens; one bad doc rejects
// everyone else's work along with it).
// ---------------------------------------------------------------------------

export function computeBlockingReasons(draft: DocDraft, effectiveVendor: VendorEffective): string[] {
  const reasons: string[] = [];
  if (!draft.orderDate) reasons.push('Needs an order date.');
  if (effectiveVendor.kind === 'needs-name') {
    reasons.push('Needs a vendor: type one below, or resolve it in the vendor triage panel.');
  } else if (effectiveVendor.kind === 'group' && !effectiveVendor.resolved) {
    reasons.push('Its vendor group needs a type, in the vendor triage panel below.');
  }
  const includedLines = draft.lines.filter((l) => l.included).length;
  const attachCount = Object.values(draft.attachSelected).filter(Boolean).length;
  if (includedLines === 0 && attachCount === 0) {
    reasons.push('Nothing to import: every line is excluded and nothing is selected to attach.');
  }
  // The not-sure gate, said the same way the server will say it. Blocking here
  // is not politeness: confirm() rejects the WHOLE batch over one unanswered
  // entry, so letting it leave the browser would cost everyone else's work too.
  const waiting = unreconciledReviewKeys(draft).length;
  if (waiting > 0) {
    reasons.push(
      `${waiting} ${waiting === 1 ? 'thing the reader was not sure of is' : 'things the reader was not sure of are'} still waiting on you. Fix each one, or mark it as looking right.`
    );
  }
  // The other product's purchase order, unanswered. Same all-or-nothing reason as
  // the gate above: one unticked box would reject every other document in the
  // batch along with it.
  //
  // Phrased as an imperative like every sibling in this list. A noun fragment
  // ("SwarmSystems purchase order.") reads as a label in a box where everything
  // else is a to-do.
  // `original.foreign_entity` rather than a re-derivation from the number, and that
  // is safe HERE specifically: this lane renders the PO number as text, never as an
  // input, so the number the server judged at parse is the number confirm will
  // receive. The scan lane cannot do this — its number is editable, so it derives
  // from the live field (see foreignEntityFor in poImportApi.ts).
  if (draft.original.foreign_entity && !draft.foreignAck) {
    reasons.push(`Needs a yes: ${draft.poNumber} was raised by ${PRODUCT_LABEL[draft.original.foreign_entity]}.`);
  }
  return reasons;
}

// ---------------------------------------------------------------------------
// Building the confirm() payload
// ---------------------------------------------------------------------------

/**
 * What a vendor GROUP currently resolves to, independent of any one document.
 * Exported because both the confirm-payload builder below and the vendor
 * preview modal need the exact same answer — the reviewer's create/existing
 * decision in the triage panel IS the thing being previewed, so this is the
 * one function computing it.
 */
export function vendorFieldsForGroup(
  group: VendorGroup,
  resolution: VendorGroupResolution,
  vendorList: VendorOption[]
): Pick<ConfirmDocumentInput, 'vendor_id' | 'vendor_create' | 'vendor_name'> {
  const byId = new Map(vendorList.map((v) => [v.id, v]));

  if (resolution.mode === 'existing' && resolution.existingVendorId) {
    return {
      vendor_id: resolution.existingVendorId,
      vendor_create: null,
      vendor_name: byId.get(resolution.existingVendorId)?.name || null,
    };
  }
  const seed = group.seedFields;
  const create: ConfirmVendorCreate = {
    name: resolution.name || seed.name,
    type: resolution.type,
    contact_name: seed.contact_name ?? null,
    contact_email: seed.contact_email ?? null,
    contact_phone: seed.contact_phone ?? null,
    address_line1: seed.address_line1 ?? null,
    address_line2: seed.address_line2 ?? null,
    city: seed.city ?? null,
    region: seed.region ?? null,
    postal_code: seed.postal_code ?? null,
    country: seed.country ?? null,
    payment_terms: seed.payment_terms ?? null,
    is_disabled: resolution.isDisabled,
  };
  return { vendor_id: null, vendor_create: create, vendor_name: create.name };
}

export function vendorPayloadFields(
  effectiveVendor: VendorEffective,
  vendorList: VendorOption[]
): Pick<ConfirmDocumentInput, 'vendor_id' | 'vendor_create' | 'vendor_name' | 'vendor_enrichment'> {
  const byId = new Map(vendorList.map((v) => [v.id, v]));

  if (effectiveVendor.kind === 'matched' || effectiveVendor.kind === 'manual-existing') {
    return {
      vendor_id: effectiveVendor.vendorId,
      vendor_create: null,
      vendor_name: byId.get(effectiveVendor.vendorId)?.name || effectiveVendor.label || null,
      vendor_enrichment: null,
    };
  }

  if (effectiveVendor.kind === 'group') {
    return { ...vendorFieldsForGroup(effectiveVendor.group, effectiveVendor.resolution, vendorList), vendor_enrichment: null };
  }

  return { vendor_id: null, vendor_create: null, vendor_name: null, vendor_enrichment: null };
}

/**
 * A matched document's enrichment proposal is forwarded verbatim and applied
 * unconditionally when the automatic match was accepted as-is — it only ever
 * fills columns the vendor row still has empty, per vendors-service.js, so
 * there is nothing for a reviewer to decide there (see po-import-service.js
 * _resolveVendor's doc comment).
 *
 * BUT the proposal was computed against the ORIGINALLY matched vendor id.
 * If the reviewer overrode the match (existingVendorOverrideId set — "that
 * was the wrong company"), the enrichment's vendor_id still points at the
 * vendor that got overridden, not the one actually being used. Forwarding it
 * anyway would enrich a vendor row this document no longer has anything to
 * do with, so an override discards the proposal instead of misdirecting it.
 */
export function enrichmentFor(draft: DocDraft, effectiveVendor: VendorEffective) {
  if (effectiveVendor.kind !== 'matched' || draft.existingVendorOverrideId) return null;
  const enrichment = draft.original.vendor_resolution?.enrichment;
  return enrichment ? { vendor_id: enrichment.vendor_id, fields: enrichment.fields } : null;
}

export function buildConfirmInput(
  draft: DocDraft,
  effectiveVendor: VendorEffective,
  vendorList: VendorOption[]
): ConfirmDocumentInput {
  const vendorFields = vendorPayloadFields(effectiveVendor, vendorList);
  const source: ImportSource = isAiSource(draft.original.source) ? draft.original.source : 'xlsx';
  const aiRead = isAiSource(source);

  return {
    source_filename: draft.key,
    source,
    po_number: draft.poNumber,
    status: draft.status,
    order_date: draft.orderDate,
    quote_number: draft.quoteNumber || null,
    ship_date: draft.shipDate || null,
    ship_via: draft.shipVia || null,
    terms: draft.terms || null,
    ship_to_name: draft.shipToName || null,
    ship_to_attn: draft.shipToAttn || null,
    ship_to_address: draft.shipToAddress || null,
    notes: draft.notes || null,
    subtotal_cents: draft.subtotalCents,
    vendor_id: vendorFields.vendor_id,
    vendor_create: vendorFields.vendor_create,
    vendor_name: vendorFields.vendor_name,
    vendor_enrichment: enrichmentFor(draft, effectiveVendor),
    lines: draft.lines
      .filter((l) => l.included)
      .map((l) => ({
        quantity: l.quantity,
        line_type: l.line_type,
        unit_cost_cents: l.unit_cost_cents,
        part_catalog_id: l.part_catalog_id,
        description: l.description,
        unit_price_text: l.unit_price_text,
        unit_price_exact: l.unit_price_exact,
      })),
    attach_line_ids: Object.entries(draft.attachSelected)
      .filter(([, checked]) => checked)
      .map(([id]) => id),
    // Sent only for the document that was actually asked. The server re-derives
    // the question from the PO number, so an unasked-for `true` changes nothing.
    foreign_instance_ack: draft.original.foreign_entity ? draft.foreignAck : undefined,
    amended_from_po_number: draft.supersedesPoNumber.trim() || null,
    applied_corrections: draft.original.workbook_overlay.applied_corrections,
    workbook_flags: draft.original.workbook_overlay.flags,
    staged_extraction: aiRead ? (draft.original.extraction as NonNullable<StagedDocument['extraction']>) : undefined,
    // The three inputs the server re-derives review_required from, plus the
    // answers. Sent only for the lane that has anything to reconcile.
    staged_confidence: aiRead ? draft.original.confidence : undefined,
    staged_warnings: aiRead ? draft.original.warnings : undefined,
    reviews: aiRead ? reviewPayload(draft) : undefined,
  };
}

/**
 * What the INCLUDED lines add up to. This is the same arithmetic the server
 * applies to the lines it receives (po-import-service.js _validateDocument) and
 * the same convention the rest of the PO module holds: subtotal_cents IS the sum
 * of every line, charges included.
 */
export function subtotalFromLines(lines: ImportLine[]): number {
  return lines.filter((l) => l.included).reduce((sum, l) => sum + l.quantity * (l.unit_cost_cents || 0), 0);
}

/**
 * Patch one line, and let the header subtotal follow it.
 *
 * Excluding a line changes what the purchase order is worth. Leaving the header
 * on the figure that included it files a document whose own total disagrees with
 * its own lines, which the server now records as a subtotal_vs_lines note on
 * every PO it lands — a permanent annotation caused entirely by a checkbox.
 *
 * The recompute is unconditional on an include/exclude, INCLUDING over a manual
 * edit: a reviewer who typed a subtotal and then changed which lines count has
 * changed the basis for that number, and the excluded-line arithmetic is the
 * better default of the two. Nothing is lost, since the field stays editable and
 * the card shows the computed figure inline whenever the two differ. Any other
 * line patch (line_type, say) leaves the subtotal alone: it does not move the sum.
 */
export function patchLineInDraft(draft: DocDraft, index: number, patch: Partial<ImportLine>): DocDraft {
  const lines = draft.lines.map((l, i) => (i === index ? { ...l, ...patch } : l));
  return {
    ...draft,
    lines,
    subtotalCents: 'included' in patch ? subtotalFromLines(lines) : draft.subtotalCents,
  };
}

// ---------------------------------------------------------------------------
// Vendor preview — shared by the document card and the vendor triage panel.
// The harvested/proposed vendor data flows through this whole module already;
// these two functions are the only new surface a preview needed, both of them
// pure adapters onto shapes that already exist for the confirm payload.
// ---------------------------------------------------------------------------

/**
 * A create_proposal is the server's own shape (VendorCreateProposal, keyed
 * `vendor_type`); ConfirmVendorCreate is what confirm() and the preview modal
 * both expect (keyed `type`). Only reached for a document that named a vendor
 * but is not (yet) part of any triage group — still unchecked, so it never
 * entered buildGroupInputs and has no VendorGroupResolution to read instead.
 */
export function vendorCreateFromProposal(cp: VendorCreateProposal): ConfirmVendorCreate {
  return {
    name: cp.name,
    type: cp.vendor_type || '',
    contact_name: cp.contact_name ?? null,
    contact_email: cp.contact_email ?? null,
    contact_phone: cp.contact_phone ?? null,
    address_line1: cp.address_line1 ?? null,
    address_line2: cp.address_line2 ?? null,
    city: cp.city ?? null,
    region: cp.region ?? null,
    postal_code: cp.postal_code ?? null,
    country: cp.country ?? null,
    payment_terms: cp.payment_terms ?? null,
    is_disabled: cp.is_disabled,
  };
}

/** The preview subject for a triage group's CURRENT decision — an existing
 * vendor once linked, otherwise whatever the create proposal presently reads
 * (possibly still missing a type; the modal shows that as "not chosen yet"
 * rather than pretending there is nothing to preview yet). */
export function previewSubjectForGroup(
  group: VendorGroup,
  resolution: VendorGroupResolution,
  vendorList: VendorOption[]
): VendorPreviewSubject {
  const fields = vendorFieldsForGroup(group, resolution, vendorList);
  return fields.vendor_id
    ? { mode: 'existing', vendorId: fields.vendor_id, vendorName: fields.vendor_name || undefined }
    : { mode: 'proposal', proposal: fields.vendor_create as ConfirmVendorCreate };
}

/** Read the API's { error: { code, message, ... } } body the same way every
 * other PO surface does. */
export function importErrorMessage(err: unknown): string {
  const e = err as { data?: ApiErrorBody; message?: string } | undefined;
  return e?.data?.error?.message || e?.message || 'That import batch was rejected.';
}

export function importErrorPoNumber(err: unknown): string | undefined {
  const e = err as { data?: ApiErrorBody } | undefined;
  return e?.data?.error?.po_number;
}
